import React, { useState, useEffect, useCallback, useRef, useMemo, startTransition } from 'react';
import ZettelTree from './components/ZettelTree';
import ZettelEditor from './components/ZettelEditor';
import GraphView from './components/GraphView';
import QuoteView from './components/QuoteView';
import ReadwisePanel from './components/ReadwisePanel';
import SettingsModal from './components/SettingsModal';
import { fetchBootstrap, fetchZettel, fetchZettelById, fetchNextId, searchZettel, createZettel, updateZettel, deleteZettel, moveZettel, fetchReadwiseSlim, fetchHighlightDataByRefs, fetchSettings, trackInteraction, triggerReadwiseSync, fetchQuotes, checkForUpdate } from './api';

const APP_VERSION = require('../package.json').version;
import { IconPanelLeftClose, IconPanelLeftOpen, IconNetwork, IconTree, IconPlus, IconFileText,
         IconTrash, IconSun, IconMoon, IconSearch, IconLink, IconQuote, IconZettelkasten } from './components/Icons';
import { getParentId, isDescendantOf } from './utils/luhmann';
import './App.css';

function App() {
  const [zettelList, setZettelList] = useState([]);
  const [selectedZettel, setSelectedZettel] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [searchResults, setSearchResults] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newNoteMode, setNewNoteMode] = useState(false);
  const [editorFocusKey, setEditorFocusKey] = useState(0);
  const [readwiseTargetRef, setReadwiseTargetRef] = useState(null);
  const [showGraph, setShowGraph] = useState(false);
  const [structureView, setStructureView] = useState('tree');
  const [quotes, setQuotes] = useState([]);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdQuery, setCmdQuery] = useState('');
  const [libraryList, setLibraryList] = useState([]);
  const [selectedLibrarySourceId, setSelectedLibrarySourceId] = useState(null);

  // Linking
  const [linkingHighlight, setLinkingHighlight] = useState(null);
  const [linkingMode, setLinkingMode] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Keep refs in sync so stable callbacks can read current linking state
  useEffect(() => { linkingHighlightRef.current = linkingHighlight; }, [linkingHighlight]);
  useEffect(() => { linkingModeRef.current = linkingMode; }, [linkingMode]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cheatSheetOpen, setCheatSheetOpen] = useState(false);
  const [focusedPanel, setFocusedPanel] = useState(null);
  const [libraryDetailOpen, setLibraryDetailOpen] = useState(false);
  const [highlightFocused, setHighlightFocused] = useState(false);
  const [highlightEditing, setHighlightEditing] = useState(false);
  const [highlightCopied, setHighlightCopied] = useState(false);
  const highlightCopiedTimer = useRef(null);

  const handleHighlightCopied = useCallback(() => {
    setHighlightCopied(true);
    clearTimeout(highlightCopiedTimer.current);
    highlightCopiedTimer.current = setTimeout(() => setHighlightCopied(false), 3000);
  }, []);

  // Stabile Callbacks für ReadwisePanel (verhindert React.memo-Invalidierung)
  const handleHighlightRefUsed = useCallback(() => setReadwiseTargetRef(null), []);
  const handleSourceSelected = useCallback(() => setSelectedLibrarySourceId(null), []);
  const handleOpenCmd = useCallback(() => setCmdOpen(true), []);
  // Stabile Callbacks für ZettelEditor
  const handleNewNoteSaved = useCallback(() => setNewNoteMode(false), []);
  const noopCallback = useCallback(() => {}, []);

  // Phase 4: First-use shortcut tips
  const [shownTips, setShownTips] = useState(() => {
    try { return JSON.parse(localStorage.getItem('shownTips') || '{}'); } catch { return {}; }
  });
  const [shortcutTip, setShortcutTip] = useState(null);
  const shortcutTipTimer = useRef(null);

  // Zettel-Löschen: Undo-Toast + Bestätigungsdialog
  const [undoAction, setUndoAction] = useState(null);
  const undoTimerRef = useRef(null);
  const pendingDeleteRef = useRef(null); // { id, cascade } — wartender Lösch-Auftrag
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Lizenz-System: Status wird beim App-Start und nach Änderungen in Settings geprüft
  // Im Browser (Dev-Modus, kein Electron) ist alles freigeschaltet
  const [licenseStatus, setLicenseStatus] = useState(null);
  const isElectron = !!window.electronAPI?.license;
  const isLicensed = !isElectron || licenseStatus?.state === 'licensed' || licenseStatus?.state === 'trial';

  // Update-Banner (#87): Prüft ob neuere Version verfügbar ist
  const [updateInfo, setUpdateInfo] = useState(null);
  useEffect(() => {
    // Nicht prüfen wenn der Nutzer den Banner innerhalb der letzten 3 Tage geschlossen hat
    const dismissed = localStorage.getItem('updateDismissed');
    if (dismissed && Date.now() - parseInt(dismissed, 10) < 3 * 24 * 60 * 60 * 1000) return;
    // Verzögert prüfen — App soll erst fertig laden
    const timer = setTimeout(() => {
      checkForUpdate(APP_VERSION).then(info => { if (info) setUpdateInfo(info); });
    }, 5000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  // Lizenzstatus beim App-Start laden (nur in Electron)
  useEffect(() => {
    if (!window.electronAPI?.license) return;
    window.electronAPI.license.getStatus()
      .then(status => setLicenseStatus(status))
      .catch(err => console.error('[License] Status-Abfrage fehlgeschlagen:', err));
    // Schicht 2: Server-Check im Hintergrund (verzögert, non-blocking)
    const timer = setTimeout(() => {
      window.electronAPI.license.serverCheck?.()
        .then(() => window.electronAPI.license.getStatus())
        .then(status => { if (status) setLicenseStatus(status); })
        .catch(() => { /* intentional: Server nicht erreichbar ist OK */ });
    }, 3000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  // Callback für SettingsModal: Lizenzstatus aktualisieren nach Aktivierung/Deaktivierung
  const handleLicenseChange = useCallback((status) => {
    setLicenseStatus(status);
  }, []);

  // Navigation history for back/forward button
  const navHistory = useRef([]);
  const [canGoBack, setCanGoBack] = useState(false);
  const forwardHistory = useRef([]);
  const [canGoForward, setCanGoForward] = useState(false);

  const cmdInputRef = useRef(null);
  const treeRef = useRef(null);
  const sidebarBeforeGraph = useRef(false);
  const flushSaveRef = useRef(null);
  // Refs so linking callbacks stay stable without re-creating on state change
  const linkingModeRef = useRef(null);
  const linkingHighlightRef = useRef(null);

  // Auto-collapse sidebar when switching to graph, restore when switching back
  useEffect(() => {
    if (structureView === 'graph') {
      sidebarBeforeGraph.current = sidebarCollapsed;
      startTransition(() => setSidebarCollapsed(true));
    } else {
      startTransition(() => setSidebarCollapsed(sidebarBeforeGraph.current));
    }
  }, [structureView]); // eslint-disable-line

  // Load quotes when switching to quotes view or when zettel change
  useEffect(() => {
    if (structureView === 'quotes') {
      fetchQuotes().then(setQuotes).catch(e => console.error('Zitate laden fehlgeschlagen:', e));
    }
  }, [structureView, refreshKey]);

  // Scroll-Position des Baums merken/wiederherstellen beim Ansichts-Wechsel
  const treeScrollPos = useRef(0);
  const prevStructureView = useRef(structureView);

  useEffect(() => {
    const wasTree = prevStructureView.current === 'tree';
    const isTree = structureView === 'tree';
    prevStructureView.current = structureView;

    // Scroll-Position merken wenn Baum verlassen wird
    if (wasTree && !isTree) {
      const scrollArea = document.querySelector('.tree-scroll-area');
      if (scrollArea) treeScrollPos.current = scrollArea.scrollTop;
    }
    // Scroll-Position wiederherstellen wenn Baum betreten wird
    if (isTree && !wasTree) {
      requestAnimationFrame(() => {
        const scrollArea = document.querySelector('.tree-scroll-area');
        if (scrollArea) scrollArea.scrollTop = treeScrollPos.current;
      });
    }
  }, [structureView]);

  // Fokus auf das richtige Element setzen wenn Ansicht wechselt ODER mittlere Spalte fokussiert wird
  useEffect(() => {
    if (focusedPanel !== 'tree') return;
    // requestAnimationFrame: DOM muss erst rendern (content-visibility Übergang), dann fokussieren
    requestAnimationFrame(() => {
      if (structureView === 'tree') {
        const tree = document.querySelector('.zettel-tree');
        if (tree) tree.focus();
      } else if (structureView === 'quotes') {
        const quoteView = document.querySelector('.quote-view');
        if (quoteView) quoteView.focus();
      } else {
        document.querySelector('.panel-structure')?.focus();
      }
    });
  }, [structureView, focusedPanel]);

  // Dynamisch Traffic-Light-Position anpassen wenn Sidebar ein-/ausgeklappt wird
  // Debounce: Position erst NACH der Animation setzen (kein Main-Process-Block während Animation)
  useEffect(() => {
    if (window.electronAPI?.setTrafficLightPosition) {
      const x = sidebarCollapsed ? 14 : 20;
      const timer = setTimeout(() => {
        window.electronAPI.setTrafficLightPosition(x, 18);
      }, 320);
      return () => clearTimeout(timer);
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Sync theme from Settings → AppearanceSection
  useEffect(() => {
    const handler = (e) => setTheme(e.detail);
    window.addEventListener('theme-change', handler);
    return () => window.removeEventListener('theme-change', handler);
  }, []);

  // Cleanup undo timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  // Command Palette keyboard shortcut + Panel-Wechsel Shortcuts
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(prev => !prev);
        setCmdQuery('');
      }
      // Escape-Hierarchie: immer von innen nach außen
      if (e.key === 'Escape') {
        if (cmdOpen)        { setCmdOpen(false); return; }
        if (cheatSheetOpen) { setCheatSheetOpen(false); return; }
        if (settingsOpen)   { setSettingsOpen(false); return; }
        if (linkingMode)    { cancelLinking?.(); return; }
      }
      // Cmd+/ = Cheat Sheet
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setCheatSheetOpen(prev => !prev);
      }
      // Cmd+F = Suchfeld fokussieren (Bibliothek oder Struktur)
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        const searchInput = document.querySelector('.panel-sidebar .search-input, .panel-structure .search-input');
        if (searchInput) { searchInput.focus(); searchInput.select(); }
      }
      // Cmd+, = Einstellungen
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        setSettingsOpen(true);
      }
      // Cmd+1 = Fokus auf Sidebar/Bibliothek — erneut drücken klappt zu/auf
      // Im Graph-Modus: Sidebar aufklappen + Graph verlassen (wie Klick auf zugeklappte Sidebar)
      if ((e.metaKey || e.ctrlKey) && e.key === '1') {
        e.preventDefault();
        if (structureView === 'graph') {
          sidebarBeforeGraph.current = false;
          startTransition(() => setSidebarCollapsed(false));
          setStructureView('tree');
          setFocusedPanel('library');
          document.querySelector('.panel-sidebar')?.focus();
          return;
        }
        if (focusedPanel === 'library') {
          // Bereits fokussiert → Toggle Sidebar
          startTransition(() => setSidebarCollapsed(prev => !prev));
        } else {
          // Von anderer Spalte → Bibliothek fokussieren (ggf. aufklappen)
          setFocusedPanel('library');
          if (sidebarCollapsed) startTransition(() => setSidebarCollapsed(false));
          document.querySelector('.panel-sidebar')?.focus();
        }
      }
      // Cmd+2 = Fokus auf Structure — erneut drücken wechselt Ansicht (tree → quotes → graph → tree)
      if ((e.metaKey || e.ctrlKey) && e.key === '2') {
        e.preventDefault();
        if (focusedPanel === 'tree') {
          // Bereits fokussiert → nächste Ansicht
          setStructureView(prev => prev === 'tree' ? 'quotes' : prev === 'quotes' ? 'graph' : 'tree');
        } else {
          // Von anderer Spalte → mittlere Spalte fokussieren (Ansicht bleibt)
          setFocusedPanel('tree');
        }
      }
      // Cmd+3 = Fokus auf Content/Editor (bei vorhandenem Titel → direkt in CM-Editor)
      if ((e.metaKey || e.ctrlKey) && e.key === '3') {
        e.preventDefault();
        setFocusedPanel('editor');
        setEditorFocusKey(k => k + 1);
        // DOM-Fokus auf Editor-Panel setzen (auch wenn kein Zettel offen)
        requestAnimationFrame(() => {
          const cm = document.querySelector('.panel-content .cm-editor .cm-content');
          if (cm) { cm.focus(); } else { document.querySelector('.panel-content')?.focus(); }
        });
      }
      // Cmd+N = Neuen Zettel erstellen (Create-Modus) — nur wenn lizenziert
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        if (isLicensed) treeRef.current?.triggerCreateMode();
      }
      // Enter = Sidebar aufklappen wenn collapsed + fokussiert
      if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key === 'Enter' && sidebarCollapsed && focusedPanel === 'library') {
        e.preventDefault();
        startTransition(() => setSidebarCollapsed(false));
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [cmdOpen, cheatSheetOpen, settingsOpen, linkingMode, sidebarCollapsed, focusedPanel, structureView]); // eslint-disable-line

  // Focus cmd input when opened
  useEffect(() => {
    if (cmdOpen && cmdInputRef.current) {
      setTimeout(() => cmdInputRef.current?.focus(), 50);
    }
  }, [cmdOpen]);

  // PERF-001: Bootstrap-State — ReadwisePanel wird erst gerendert wenn Daten da sind
  const [bootstrapReady, setBootstrapReady] = useState(false);
  const [initialHighlightZettelMap, setInitialHighlightZettelMap] = useState({});
  const [initialHighlightStatus, setInitialHighlightStatus] = useState({});
  const [initialSyncing, setInitialSyncing] = useState(false);

  // Bootstrap: Alle Startup-Daten in einem Request statt 7 einzelne
  const bootstrapDone = useRef(false);
  const loadZettel = useCallback(async () => {
    try {
      setZettelList(await fetchZettel());
    } catch (err) {
      console.error('Zettel-Liste laden fehlgeschlagen:', err);
    }
  }, []);

  // Load library (Literaturnotizen) — slim: nur Metadaten, keine Highlights
  const refreshLibrary = useCallback(() => {
    fetchReadwiseSlim().then(setLibraryList).catch(err => console.error('Bibliothek laden fehlgeschlagen:', err));
  }, []);

  useEffect(() => {
    if (bootstrapDone.current) return;
    bootstrapDone.current = true;
    // === LEVEL 4: App-Startup Interaction Tracking ===
    const startup = trackInteraction('app-startup');
    startup.step('bootstrap-fetch-start');
    fetchBootstrap().then(data => {
      startup.step('bootstrap-fetched');
      setZettelList(data.zettel || []);
      setLibraryList(data.readwiseSlim || []);
      setInitialHighlightZettelMap(data.highlightZettelMap || {});
      setInitialHighlightStatus(data.highlightStatus || {});
      setInitialSyncing(data.syncStatus?.syncing || false);
      if (!data.settings?.readwise?.has_api_key) setSettingsOpen(true);
      startup.step('state-updated');
      setBootstrapReady(true);
      startup.endAtPaint(); // Misst bis der Browser tatsächlich gerendert hat
    }).catch(err => {
      console.error('Bootstrap fehlgeschlagen, nutze Fallback:', err);
      startup.step('bootstrap-failed-fallback');
      loadZettel();
      refreshLibrary();
      fetchSettings().then(s => {
        if (!s?.readwise?.has_api_key) setSettingsOpen(true);
      }).catch(err2 => console.error('Einstellungen laden fehlgeschlagen:', err2));
      setBootstrapReady(true);
      startup.endAtPaint();
    });
  }, []); // eslint-disable-line

  // SSE: Echtzeit-Updates bei externen Dateiänderungen (Finder, Editor etc.)
  // Ref für selectedZettel-ID — vermeidet Stale-Closure ohne die Verbindung neu aufzubauen
  const selectedZettelIdRef = useRef(null);
  useEffect(() => { selectedZettelIdRef.current = selectedZettel?.id ?? null; }, [selectedZettel]);

  useEffect(() => {
    const es = new EventSource('/api/events');

    es.addEventListener('zettel-changed', async (e) => {
      try {
        const { id } = JSON.parse(e.data);
        const updated = await fetchZettelById(id);
        if (!updated) return;
        // Zettel-Liste sofort aktualisieren (Titel, Tags, Links)
        setZettelList(prev => {
          const exists = prev.some(z => z.id === id);
          const entry = { id: updated.id, title: updated.title, tags: updated.tags || [], links: updated.links || [], modified: updated.modified, type: updated.type || 'permanent' };
          if (exists) return prev.map(z => z.id === id ? { ...z, ...entry } : z);
          return [...prev, entry]; // neu angelegte Datei
        });
        // Falls gerade dieser Zettel geöffnet ist: Content ebenfalls aktualisieren
        if (selectedZettelIdRef.current === id) {
          setSelectedZettel(updated);
        }
      } catch (err) {
        console.error('SSE zettel-changed Fehler:', err);
      }
    });

    es.addEventListener('zettel-deleted', (e) => {
      try {
        const { id } = JSON.parse(e.data);
        setZettelList(prev => prev.filter(z => z.id !== id));
        if (selectedZettelIdRef.current === id) setSelectedZettel(null);
      } catch (err) {
        console.error('SSE zettel-deleted Fehler:', err);
      }
    });

    // SSE: Readwise-Sync abgeschlossen → Bibliothek aktualisieren
    es.addEventListener('readwise-sync-done', () => {
      refreshLibrary();
    });

    es.onerror = () => { /* EventSource reconnect automatisch nach Verbindungsverlust */ };
    return () => es.close();
  }, []); // eslint-disable-line

  // PERF-007: Highlight-Daten on-demand — nur benötigte Refs laden statt 9MB komplett
  const highlightDataCache = useRef(new Map());
  const pendingRefs = useRef(new Set());
  const fetchTimer = useRef(null);
  const [hlDataVersion, setHlDataVersion] = useState(0);

  const flushPendingRefs = useCallback(() => {
    if (pendingRefs.current.size === 0) return;
    const refs = [...pendingRefs.current];
    pendingRefs.current.clear();
    fetchHighlightDataByRefs(refs).then(data => {
      let changed = false;
      for (const [ref, val] of Object.entries(data)) {
        highlightDataCache.current.set(ref, val);
        changed = true;
      }
      if (changed) setHlDataVersion(v => v + 1); // Trigger re-render
    }).catch(err => console.error('Highlight-Daten laden fehlgeschlagen:', err));
  }, []);

  const getHighlightData = useCallback((ref) => {
    const cached = highlightDataCache.current.get(ref);
    if (cached) return cached;
    // Ref noch nicht geladen — in Queue packen und batch-fetchen
    if (!pendingRefs.current.has(ref)) {
      pendingRefs.current.add(ref);
      if (fetchTimer.current) clearTimeout(fetchTimer.current);
      fetchTimer.current = setTimeout(flushPendingRefs, 50); // 50ms Debounce für Batching
    }
    return null;
  }, [flushPendingRefs, hlDataVersion]); // eslint-disable-line

  // Merge a newly created zettel into the local list without a full refetch
  const mergeNewZettel = useCallback((z) => {
    setZettelList(prev => [...prev, {
      id: z.id, title: z.title, tags: z.tags || [], links: z.links || [],
      wikilinks: [], type: z.type || 'permanent',
      created: z.created, modified: z.modified, snippet: ''
    }]);
  }, []);

  // Remove a zettel from local list without a full refetch
  const removeZettelFromList = useCallback((id) => {
    setZettelList(prev => prev.filter(z => z.id !== id));
  }, []);

  const handleSelectZettel = useCallback(async (id) => {
    // === LEVEL 4: Zettel-Select Interaction Tracking ===
    const interaction = trackInteraction('zettel-select');
    interaction.step('click');
    if (flushSaveRef.current) flushSaveRef.current();
    interaction.step('save-flushed');
    setSelectedZettel(prev => {
      if (prev && prev.id !== id) {
        navHistory.current = [...navHistory.current, prev.id];
        if (navHistory.current.length > 50) navHistory.current = navHistory.current.slice(-50);
        setCanGoBack(true);
        // Normal navigation clears forward history
        forwardHistory.current = [];
        setCanGoForward(false);
      }
      return prev;
    });
    try {
      interaction.step('fetch-start');
      const zettel = await fetchZettelById(id);
      interaction.step('fetched');
      // Echtzeit-Graph: Vorherigen Link-Stand tracken für Diff bei Save
      prevLinksRef.current[zettel.id] = zettel.links || [];
      setSelectedZettel(zettel);
      setIsEditing(false);
      interaction.endAtPaint(); // Misst bis der Zettel tatsächlich gerendert ist
    } catch (err) {
      console.error('Zettel laden fehlgeschlagen:', err);
    }
  }, []); // eslint-disable-line

  const handleGoBack = useCallback(async () => {
    if (navHistory.current.length === 0) return;
    if (flushSaveRef.current) flushSaveRef.current();
    const prevId = navHistory.current[navHistory.current.length - 1];
    navHistory.current = navHistory.current.slice(0, -1);
    setCanGoBack(navHistory.current.length > 0);
    // Push current zettel onto forward history before navigating back
    setSelectedZettel(prev => {
      if (prev) {
        forwardHistory.current = [...forwardHistory.current, prev.id];
        setCanGoForward(true);
      }
      return prev;
    });
    try {
      const zettel = await fetchZettelById(prevId);
      setSelectedZettel(zettel);
      setIsEditing(false);
    } catch (err) {
      console.error('Zurück-Navigation fehlgeschlagen:', err);
    }
  }, []); // eslint-disable-line

  const handleGoForward = useCallback(async () => {
    if (forwardHistory.current.length === 0) return;
    if (flushSaveRef.current) flushSaveRef.current();
    const nextId = forwardHistory.current[forwardHistory.current.length - 1];
    forwardHistory.current = forwardHistory.current.slice(0, -1);
    setCanGoForward(forwardHistory.current.length > 0);
    // Push current zettel onto back history before navigating forward
    setSelectedZettel(prev => {
      if (prev) {
        navHistory.current = [...navHistory.current, prev.id];
        setCanGoBack(true);
      }
      return prev;
    });
    try {
      const zettel = await fetchZettelById(nextId);
      setSelectedZettel(zettel);
      setIsEditing(false);
    } catch (err) {
      console.error('Vorwärts-Navigation fehlgeschlagen:', err);
    }
  }, []); // eslint-disable-line

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) { setSearchResults(null); return; }
    try {
      setSearchResults(await searchZettel(query));
    } catch (err) {
      console.error('Suche fehlgeschlagen:', err);
    }
  }, []);

  const handleOpenReadwise = useCallback((ref) => {
    setReadwiseTargetRef(ref);
    startTransition(() => setSidebarCollapsed(false));
    sidebarBeforeGraph.current = false;
    setStructureView('tree');
  }, []); // eslint-disable-line

  // Echtzeit-Sync: Patch-Objekt für ZettelEditor
  // Kein Full-Refetch, kein setSelectedZettel → kein Cursor-Verlust, kein Flicker,
  // keine Race-Condition mit AutoSave
  const [highlightPatch, setHighlightPatch] = useState(null);
  const handleHighlightEdited = useCallback((highlightRef, newText) => {
    if (!highlightRef || newText === undefined) return;
    // Neues Objekt erzeugen, damit useEffect im ZettelEditor immer feuert
    setHighlightPatch({ ref: highlightRef, text: newText, _ts: Date.now() });
  }, []);

  // Echtzeit-Graph: Knoten und Kanten inkrementell aktualisieren ohne Refetch.
  // Events: { added/removed (Links), addedNodes/removedNodes (Knoten), ts }
  const [graphLinkEvent, setGraphLinkEvent] = useState(null);
  const prevLinksRef = useRef({}); // { zettelId: [linkId, ...] } — vorheriger Stand für Diff

  // Echtzeit-Unlink: Wenn ein Highlight aus einer Notiz entfernt wird (Drag-out oder Papierkorb),
  // wird ReadwisePanel sofort informiert — der Link-Chip verschwindet ohne Reload.
  const [highlightUnlinkEvent, setHighlightUnlinkEvent] = useState(null);
  const handleHighlightUnlinked = useCallback((zettelId, highlightRef) => {
    setHighlightUnlinkEvent({ zettelId, highlightRef, ts: Date.now() });
  }, []);

  // Echtzeit-Link: Wenn ein Highlight via ⌘V in eine Notiz eingefügt wird,
  // wird ReadwisePanel sofort informiert — der Link-Chip erscheint ohne Reload.
  const [highlightLinkEvent, setHighlightLinkEvent] = useState(null);
  const handleHighlightLinked = useCallback((zettelId, highlightRef) => {
    setHighlightLinkEvent({ zettelId, highlightRef, ts: Date.now() });
  }, []);

  const cancelLinking = useCallback(() => {
    setLinkingHighlight(null);
    setLinkingMode(null);
  }, []);

  // Stabiler onSaved-Callback für ZettelEditor (nach cancelLinking wegen TDZ)
  const handleEditorSaved = useCallback((savedZettel) => {
    if (savedZettel) {
      const newLinks = savedZettel.links || [];
      const prevLinks = prevLinksRef.current[savedZettel.id] || [];

      // Echtzeit-Graph-Update: Diff berechnen
      const prevSet = new Set(prevLinks.map(String));
      const newSet = new Set(newLinks.map(String));
      const added = newLinks.filter(l => !prevSet.has(String(l))).map(t => ({ source: savedZettel.id, target: String(t), type: 'explicit' }));
      const removed = prevLinks.filter(l => !newSet.has(String(l))).map(t => ({ source: savedZettel.id, target: String(t) }));

      if (added.length > 0 || removed.length > 0) {
        setGraphLinkEvent({ added, removed, ts: Date.now() });
      }
      prevLinksRef.current[savedZettel.id] = newLinks;

      setZettelList(prev => prev.map(z => z.id === savedZettel.id
        ? { ...z, title: savedZettel.title, tags: savedZettel.tags || [], links: savedZettel.links || [], modified: savedZettel.modified, type: savedZettel.type || 'permanent' }
        : z
      ));
    }
    if (linkingModeRef.current) {
      cancelLinking();
      setRefreshKey(k => k + 1);
    }
  }, [cancelLinking]);

  // --- Phase 4: Shortcut-Tipp anzeigen (First-Use Tooltips) ---
  const showShortcutTip = useCallback((key, message) => {
    if (shownTips[key]) return;
    if (shortcutTipTimer.current) clearTimeout(shortcutTipTimer.current);
    setShortcutTip({ key, message });
    const newShown = { ...shownTips, [key]: true };
    setShownTips(newShown);
    localStorage.setItem('shownTips', JSON.stringify(newShown));
    shortcutTipTimer.current = setTimeout(() => setShortcutTip(null), 4000);
  }, [shownTips]);

  // --- Toast-Notification für Drag & Drop Feedback ---
  const [dropToast, setDropToast] = useState(null);
  const dropToastTimer = useRef(null);
  const showDropToast = useCallback((msg, type = 'success') => {
    if (dropToastTimer.current) clearTimeout(dropToastTimer.current);
    setDropToast({ msg, type });
    dropToastTimer.current = setTimeout(() => setDropToast(null), type === 'error' ? 4000 : 2500);
  }, []);

  // --- Drag & Drop: Highlight aus Bibliothek in Tree oder Editor ---
  const handleHighlightDrop = useCallback(async (highlightData, dropPos) => {
    if (!isLicensed) return;
    // Autosave flushen, um Race-Condition mit Editor-Inhalten zu vermeiden
    if (flushSaveRef.current) flushSaveRef.current();

    const { ref, author, title } = highlightData;
    const marker = `{{highlight:${ref}}}`;

    try {
      if (dropPos.type === 'onto') {
        // Highlight in bestehenden Zettel einfügen (am Ende)
        const zettel = await fetchZettelById(dropPos.id);
        const existingContent = (zettel.content || '').trimEnd();
        // Duplikat-Check: Ist dieses Highlight bereits in der Notiz?
        if (existingContent.includes(`{{highlight:${ref}}}`)) {
          showDropToast('Highlight ist bereits in dieser Notiz verknüpft', 'error');
          return;
        }
        const appendText = `\n${marker}`;
        const cursorPos = existingContent.length + 1;
        await updateZettel(dropPos.id, {
          highlight_ref: ref,
          source: `${author}: ${title}`,
          content: existingContent + appendText
        });
        setRefreshKey(k => k + 1);
        const updated = await fetchZettelById(dropPos.id);
        setSelectedZettel({ ...updated, _cursorAt: cursorPos });
        setIsEditing(true);
        showDropToast(`Highlight in Zettel ${dropPos.id} eingefügt`);
      } else if (dropPos.type === 'after') {
        // "after" = Geschwister-Zettel erstellen (Kind des gleichen Elternteils)
        const parentId = getParentId(dropPos.id) || undefined;
        const newZettel = await createZettel({
          title: '', content: `\n${marker}`, type: 'permanent',
          tags: [], links: [],
          source: `${author}: ${title}`,
          highlight_ref: ref,
          parentId: parentId || undefined,
        });
        setRefreshKey(k => k + 1);
        if (newZettel?.id) {
          mergeNewZettel(newZettel);
          const z = await fetchZettelById(newZettel.id);
          setSelectedZettel(z);
          setIsEditing(true);
          showDropToast(`Neuer Zettel ${newZettel.id} mit Highlight erstellt`);
        }
      } else if (dropPos.type === 'newIndex') {
        const newZettel = await createZettel({
          title: '', content: `\n${marker}`, type: 'permanent',
          tags: [], links: [],
          source: `${author}: ${title}`,
          highlight_ref: ref,
        });
        setRefreshKey(k => k + 1);
        if (newZettel?.id) {
          mergeNewZettel(newZettel);
          const z = await fetchZettelById(newZettel.id);
          setSelectedZettel(z);
          setIsEditing(true);
          showDropToast(`Neuer Index ${newZettel.id} mit Highlight erstellt`);
        }
      } else if (dropPos.type === 'editor' && dropPos.zettelId) {
        const zettel = await fetchZettelById(dropPos.zettelId);
        const existingContent = (zettel.content || '').trimEnd();
        if (existingContent.includes(`{{highlight:${ref}}}`)) {
          showDropToast('Highlight ist bereits in dieser Notiz verknüpft', 'error');
          return;
        }
        const appendText = `\n${marker}`;
        await updateZettel(dropPos.zettelId, {
          highlight_ref: ref,
          source: `${author}: ${title}`,
          content: existingContent + appendText
        });
        setRefreshKey(k => k + 1);
        const updated = await fetchZettelById(dropPos.zettelId);
        setSelectedZettel(updated);
        showDropToast('Highlight im Editor eingefügt');
      }
    } catch (err) {
      console.error('Highlight-Drop-Fehler:', err);
      showDropToast('Highlight konnte nicht eingefügt werden', 'error');
    }
  }, [mergeNewZettel, showDropToast]);

  // --- Bibliothek-Unlink: {{highlight:ref}} Marker aus Content entfernen + highlight_ref aus Frontmatter ---
  const handleUnlinkHighlightFromNote = useCallback(async (zettelId, highlightRef) => {
    if (!isLicensed) return;
    try {
      const zettel = await fetchZettelById(zettelId);
      const oldContent = zettel.content || '';
      const hasMarker = oldContent.includes(`{{highlight:${highlightRef}}}`);

      // Content bereinigen (Marker entfernen, falls vorhanden)
      let newContent = oldContent;
      if (hasMarker) {
        const escaped = highlightRef.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const markerRegex = new RegExp(`(\\n---\\n)?\\s*\\{\\{highlight:${escaped}\\}\\}`, 'g');
        newContent = oldContent.replace(markerRegex, '').replace(/\n{3,}/g, '\n\n').replace(/^\n---\n/, '').replace(/\n---\n\s*$/, '').trimEnd();
      }

      // remove_highlight_ref entfernt nur DIESE eine Ref (nicht alle)
      const updateData = { remove_highlight_ref: highlightRef };
      if (hasMarker) updateData.content = newContent;
      await updateZettel(zettelId, updateData);

      if (selectedZettel && selectedZettel.id === zettelId) {
        const updated = await fetchZettelById(zettelId);
        setSelectedZettel(updated);
      }
      setRefreshKey(k => k + 1);
    } catch (e) {
      console.error('Fehler beim Entfernen des Highlights aus Notiz:', e);
    }
  }, [selectedZettel]);

  // --- Linking Workflow ---
  const handleLinkHighlight = useCallback(async (highlight, action) => {
    if (!isLicensed) return;
    if (action === 'newBranch') {
      const marker = `{{highlight:${highlight.ref}}}`;
      try {
        const newZettel = await createZettel({
          title: '', content: `\n${marker}`, type: 'permanent',
          tags: [], links: [],
          source: `${highlight.author}: ${highlight.title}`,
          highlight_ref: highlight.ref,
        });
        setRefreshKey(k => k + 1);
        if (newZettel?.id) {
          mergeNewZettel(newZettel);
          const z = await fetchZettelById(newZettel.id);
          setSelectedZettel(z);
          setIsEditing(true);
        }
      } catch (err) {
        console.error('Zettel erstellen fehlgeschlagen (newBranch):', err);
      }
      return;
    }
    setLinkingHighlight(highlight);
    setLinkingMode(action);
  }, [mergeNewZettel]);


  const handleTreeSelect = useCallback(async (id) => {
    // Read current linking state via refs to keep this callback stable
    const curLinkingMode = linkingModeRef.current;
    const curLinkingHighlight = linkingHighlightRef.current;
    if (curLinkingMode === 'insert' && curLinkingHighlight) {
      if (!isLicensed) return;
      try {
        const zettel = await fetchZettelById(id);
        const existingContent = (zettel.content || '').trimEnd();
        const marker = `{{highlight:${curLinkingHighlight.ref}}}`;
        const appendText = `\n${marker}`;
        const cursorPos = existingContent.length + 1;
        await updateZettel(id, {
          highlight_ref: curLinkingHighlight.ref,
          source: `${curLinkingHighlight.author}: ${curLinkingHighlight.title}`,
          content: existingContent + appendText
        });
        setRefreshKey(k => k + 1);
        const updated = await fetchZettelById(id);
        setSelectedZettel({ ...updated, _cursorAt: cursorPos });
        setIsEditing(true);
      } catch (err) {
        console.error('Highlight einfügen fehlgeschlagen:', err);
      }
      cancelLinking();
    } else {
      handleSelectZettel(id);
    }
  }, [cancelLinking, handleSelectZettel]); // eslint-disable-line

  const handleTreeAdd = useCallback(async (parentId) => {
    if (!isLicensed) return;
    const curLinkingHighlight = linkingHighlightRef.current;
    if (!curLinkingHighlight) return;
    const marker = `{{highlight:${curLinkingHighlight.ref}}}`;
    try {
      const newZettel = await createZettel({
        title: '', content: `\n${marker}`, type: 'permanent',
        tags: [], links: [],
        source: `${curLinkingHighlight.author}: ${curLinkingHighlight.title}`,
        highlight_ref: curLinkingHighlight.ref,
        parentId
      });
      setRefreshKey(k => k + 1);
      if (newZettel?.id) {
        mergeNewZettel(newZettel);
        const z = await fetchZettelById(newZettel.id);
        setSelectedZettel({ ...z, _focusTitle: true });
        setIsEditing(true);
      }
    } catch (err) {
      console.error('Zettel erstellen fehlgeschlagen (handleTreeAdd):', err);
    }
    cancelLinking();
  }, [cancelLinking, mergeNewZettel]);

  // Create zettel from tree insert (parentId = null/undefined for root) — new instant-create flow
  const handleTreeCreateZettel = useCallback(async (parentId) => {
    if (!isLicensed) return; // Read-only: keine neuen Zettel
    try {
      const nextIdData = await fetchNextId(parentId || null);
      const nextId = nextIdData.nextId;
      const newZettel = await createZettel({ id: nextId, title: '', parentId: parentId || null });
      setZettelList(prev => [...prev, { id: nextId, title: '', tags: [], links: [], wikilinks: [], type: 'permanent' }]);
      prevLinksRef.current[nextId] = [];
      setSelectedZettel({ ...newZettel, id: nextId, title: '', body: '', tags: [], links: [] });
      setIsEditing(true);
      setNewNoteMode(true);
      // Echtzeit-Graph: neuen Knoten hinzufügen
      setGraphLinkEvent({
        addedNodes: [{ id: nextId, title: '', type: 'permanent', tags: [], val: 1 }],
        added: [], removed: [], ts: Date.now()
      });
    } catch (err) {
      console.error('Zettel erstellen fehlgeschlagen (handleTreeCreateZettel):', err);
    }
  }, []);

  // Move zettel to new parent (drag-and-drop)
  const handleMoveZettel = useCallback(async (sourceId, targetParentId) => {
    if (!isLicensed) return;
    try {
      const result = await moveZettel(sourceId, targetParentId);
      if (result.success && result.idMap) {
        // Reload the full zettel list
        const updated = await fetchZettel();
        setZettelList(updated);
        // If the currently selected zettel was moved, update its ID
        if (selectedZettel && result.idMap[selectedZettel.id]) {
          const newId = result.idMap[selectedZettel.id];
          const z = await fetchZettelById(newId);
          setSelectedZettel(z);
        }
        setRefreshKey(k => k + 1);
      }
    } catch (e) {
      console.error('Move-Fehler:', e);
      window.alert('Fehler beim Verschieben: ' + (e.message || e));
    }
  }, [selectedZettel]);

  // Command palette results
  const [cmdActiveIndex, setCmdActiveIndex] = useState(0);

  const paletteActions = useMemo(() => [
    { id: 'action-sidebar', label: 'Sidebar ein-/ausklappen', icon: '⬅', action: () => startTransition(() => setSidebarCollapsed(p => !p)) },
    { id: 'action-theme', label: 'Dark/Light Mode wechseln', icon: '🌓', action: () => {
      const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('theme', next);
      setTheme(next);
    }},
    ...(isLicensed ? [{ id: 'action-sync', label: 'Readwise synchronisieren', icon: '🔄', action: () => {
      triggerReadwiseSync().catch(err => console.error('Sync fehlgeschlagen:', err));
    }}] : []),
    ...(isLicensed ? [{ id: 'action-new', label: 'Neuer Index-Zettel', icon: '➕', action: () => {
      handleTreeCreateZettel(undefined);
    }}] : []),
    { id: 'action-move', label: 'Zettel verschieben', icon: '↕', action: () => {
      treeRef.current?.triggerMoveMode(selectedZettel?.id);
    }},
    { id: 'action-cheatsheet', label: 'Tastaturkürzel anzeigen', icon: '⌨', action: () => {
      setCheatSheetOpen(true);
    }},
  ], [setSidebarCollapsed, handleTreeCreateZettel, selectedZettel]); // eslint-disable-line

  const cmdResults = useMemo(() => {
    // Action-Modus: Query beginnt mit ">"
    if (cmdQuery.startsWith('>')) {
      const q = cmdQuery.slice(1).toLowerCase().trim();
      return (q
        ? paletteActions.filter(a => a.label.toLowerCase().includes(q))
        : paletteActions
      ).map(a => ({ kind: 'action', _uid: a.id, ...a }));
    }
    if (!cmdQuery.trim()) {
      // Default: 5 recent permanent + 3 recent library sources
      const permanent = zettelList.slice(0, 5).map(z => ({ kind: 'permanent', _uid: `p-${z.id}`, ...z }));
      const literatur = libraryList.slice(0, 3).map(s => ({
        kind: 'literatur', _uid: `l-${s.readwise_id}`, id: s.readwise_id,
        title: s.title, author: s.author, readwise_id: s.readwise_id,
        category: s.category, filename: s.filename,
      }));
      return [...permanent, ...literatur];
    }
    const q = cmdQuery.toLowerCase();
    const permanent = zettelList
      .filter(z => String(z.id).toLowerCase().includes(q) || z.title?.toLowerCase().includes(q))
      .slice(0, 7)
      .map(z => ({ kind: 'permanent', _uid: `p-${z.id}`, ...z }));
    const literatur = libraryList
      .filter(s => s.title?.toLowerCase().includes(q) || s.author?.toLowerCase().includes(q))
      .slice(0, 5)
      .map(s => ({
        kind: 'literatur', _uid: `l-${s.readwise_id}`, id: s.readwise_id,
        title: s.title, author: s.author, readwise_id: s.readwise_id,
        category: s.category, filename: s.filename,
      }));
    return [...permanent, ...literatur];
  }, [cmdQuery, zettelList, libraryList, paletteActions]);

  // Reset active index when results change
  useEffect(() => { setCmdActiveIndex(0); }, [cmdQuery]);

  const handleCmdSelect = (item) => {
    if (item.kind === 'action') {
      if (typeof item.action === 'function') item.action();
    } else if (item.kind === 'permanent') {
      handleSelectZettel(item.id);
    } else if (item.kind === 'literatur') {
      // Open left sidebar on the correct source
      if (sidebarCollapsed) startTransition(() => setSidebarCollapsed(false));
      if (structureView === 'graph') setStructureView('tree');
      setSelectedLibrarySourceId(item.readwise_id);
    }
    setCmdOpen(false);
    setCmdQuery('');
  };

  // Quick-Create Zettel aus Highlight (#37)
  const handleQuickCreateFromHighlight = useCallback(async (highlightData) => {
    if (!isLicensed) return;
    try {
      const title = (highlightData.text || '').slice(0, 80).trim() || 'Aus Highlight';
      const highlightBlock = `<details data-ref="${highlightData.ref}">\n<summary>Quelle: ${highlightData.sourceName || highlightData.sourceKey || 'Unbekannt'}</summary>\n${highlightData.text || ''}\n</details>`;

      const newZettel = await createZettel({
        title,
        content: highlightBlock,
        type: 'literature',
      });

      const full = await fetchZettelById(newZettel.id);
      mergeNewZettel(full);
      prevLinksRef.current[full.id] = full.links || [];
      setSelectedZettel(full);
      setIsEditing(true);
      setRefreshKey(k => k + 1);
      // Echtzeit-Graph: neuen Knoten hinzufügen
      setGraphLinkEvent({
        addedNodes: [{ id: full.id, title: full.title || '', type: full.type || 'literature', tags: full.tags || [], val: 1 }],
        added: [], removed: [], ts: Date.now()
      });
    } catch (err) {
      console.error('Quick-Create Zettel aus Highlight fehlgeschlagen:', err);
    }
  }, [mergeNewZettel]);

  // Zettel löschen — mit Kaskaden-Schutz und Referenz-Warnung
  // Vorherigen Lösch-Auftrag sofort ausführen (damit er nicht verloren geht)
  const flushPendingDelete = useCallback(() => {
    if (undoTimerRef.current && pendingDeleteRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
      const { id, cascade } = pendingDeleteRef.current;
      pendingDeleteRef.current = null;
      deleteZettel(id, cascade).catch(err => {
        console.error('Zettel löschen fehlgeschlagen:', err);
      });
    }
  }, []);

  const executeDelete = useCallback((zettelToDelete, descendants = []) => {
    const cascade = descendants.length > 0;
    const allAffected = [zettelToDelete, ...descendants];

    // WICHTIG: Vorherigen Lösch-Auftrag sofort ausführen, nicht verwerfen!
    flushPendingDelete();

    // Optimistic: sofort aus UI entfernen
    const affectedIds = new Set(allAffected.map(z => z.id));
    setSelectedZettel(null);
    setIsEditing(false);
    setZettelList(prev => prev.filter(z => !affectedIds.has(z.id)));

    // Echtzeit-Graph: Knoten entfernen (Links werden in GraphView automatisch mit entfernt)
    setGraphLinkEvent({
      removedNodes: [...affectedIds],
      added: [], removed: [], ts: Date.now()
    });

    // Pending-Delete merken (für flush bei nächstem Löschen)
    pendingDeleteRef.current = { id: zettelToDelete.id, cascade };

    // Undo-Toast
    const count = allAffected.length;
    const undoData = {
      message: count === 1
        ? `„${zettelToDelete.title || zettelToDelete.id}" in den Papierkorb verschoben`
        : `„${zettelToDelete.title || zettelToDelete.id}" und ${count - 1} Unternotiz${count - 1 > 1 ? 'en' : ''} in den Papierkorb verschoben`,
      undo: () => {
        clearTimeout(undoTimerRef.current);
        undoTimerRef.current = null;
        pendingDeleteRef.current = null; // Lösch-Auftrag verwerfen
        setZettelList(prev => [...prev, ...allAffected.map(z => ({
          id: z.id, title: z.title, tags: z.tags || [], links: z.links || [],
          wikilinks: z.wikilinks || [], type: z.type || 'permanent',
          created: z.created, modified: z.modified, snippet: z.snippet || ''
        }))]);
        setSelectedZettel(zettelToDelete);
        setUndoAction(null);
        // Echtzeit-Graph: Knoten wiederherstellen
        setGraphLinkEvent({
          addedNodes: allAffected.map(z => ({ id: z.id, title: z.title || '', type: z.type || 'permanent', tags: z.tags || [], val: (z.links || []).length + 1 })),
          added: allAffected.flatMap(z => (z.links || []).map(l => ({ source: z.id, target: String(l), type: 'explicit' }))),
          removed: [], ts: Date.now()
        });
      }
    };
    setUndoAction(undoData);

    // Nach 5s auf dem Server löschen (→ macOS-Papierkorb)
    undoTimerRef.current = setTimeout(() => {
      const pending = pendingDeleteRef.current;
      if (pending) {
        deleteZettel(pending.id, pending.cascade).catch(err => {
          console.error('Zettel löschen fehlgeschlagen:', err);
          // Rollback bei Server-Fehler
          setZettelList(prev => [...prev, ...allAffected.map(z => ({
            id: z.id, title: z.title, tags: z.tags || [], links: z.links || [],
            wikilinks: z.wikilinks || [], type: z.type || 'permanent',
            created: z.created, modified: z.modified, snippet: z.snippet || ''
          }))]);
        });
        pendingDeleteRef.current = null;
      }
      setUndoAction(null);
      undoTimerRef.current = null;
    }, 5000);
  }, [flushPendingDelete]);

  const handleDeleteZettel = useCallback(() => {
    if (!selectedZettel || !isLicensed) return;
    const zettelToDelete = { ...selectedZettel };

    // Nachkommen ermitteln
    const descendants = zettelList.filter(z => isDescendantOf(z.id, zettelToDelete.id));

    if (descendants.length > 0) {
      // Referenzen prüfen (externe Zettel, die auf betroffene IDs verlinken)
      const affectedIds = new Set([zettelToDelete.id, ...descendants.map(z => z.id)]);
      const referencedBy = zettelList.filter(z =>
        !affectedIds.has(z.id) &&
        (
          (z.links || []).some(l => affectedIds.has(l)) ||
          (z.wikilinks || []).some(w => affectedIds.has(w))
        )
      );
      // Dialog anzeigen
      setDeleteConfirm({ zettel: zettelToDelete, descendants, referencedBy });
    } else {
      // Keine Kinder → direkt mit Toast
      executeDelete(zettelToDelete);
    }
  }, [selectedZettel, zettelList, executeDelete]);

  const breadcrumbPath = useMemo(() => {
    if (!selectedZettel?.id) return [];
    const id = selectedZettel.id;
    const parts = [];
    for (let i = 1; i <= id.length; i++) {
      if (i === id.length || /[a-z]/.test(id[i]) !== /[a-z]/.test(id[i - 1])) {
        parts.push(id.slice(0, i));
      }
    }
    return parts;
  }, [selectedZettel?.id]);

  return (
    <>
      {/* Read-Only Banner bei abgelaufener Trial/Lizenz (nur in Electron) */}
      {isElectron && !isLicensed && licenseStatus && (
        <div className="readonly-banner">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span>{licenseStatus.state === 'trial_expired' ? 'Testversion abgelaufen — Lesemodus aktiv' : 'Lizenz abgelaufen — Lesemodus aktiv'}</span>
          <button className="readonly-banner-action" onClick={() => setSettingsOpen(true)}>Lizenz eingeben</button>
        </div>
      )}
      <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${structureView === 'graph' ? 'graph-mode' : ''} ${linkingMode ? 'linking-active' : ''}`}>
        {/* ===== LEFT: Sidebar ===== */}
        <aside className={`panel panel-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${focusedPanel === 'library' ? 'panel-focused' : ''}`}
          tabIndex={-1}
          onFocusCapture={() => { if (focusedPanel !== 'library') setFocusedPanel('library'); }}
          onMouseDown={(e) => {
            setFocusedPanel('library');
            // Fokus auf Sidebar setzen, damit Keyboard-Shortcuts (⌘⇧M etc.) funktionieren
            // Aber nicht wenn ein Input/Textarea/Button geklickt wird (die bekommen eigenen Fokus)
            const tag = e.target.tagName;
            if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'BUTTON') {
              // setTimeout damit der Click-Handler erst feuert
              setTimeout(() => e.currentTarget?.focus(), 0);
            }
          }}
          onClick={sidebarCollapsed ? () => {
            sidebarBeforeGraph.current = false;
            startTransition(() => setSidebarCollapsed(false));
            if (structureView === 'graph') setStructureView('tree');
          } : undefined}
        >
          <div className="sidebar-collapsed-strip">
            <span className="sidebar-collapsed-label">Bibliothek</span>
          </div>
          <div className="sidebar-header">
            <h1 className="app-title">Bibliothek</h1>
            <button className="icon-btn" onClick={() => startTransition(() => setSidebarCollapsed(true))} aria-label="Sidebar einklappen">
              <IconPanelLeftClose />
            </button>
          </div>

          {bootstrapReady && <ReadwisePanel
            sources={libraryList}
            onRefreshSources={refreshLibrary}
            initialHighlightRef={readwiseTargetRef}
            onHighlightRefUsed={handleHighlightRefUsed}
            initialSourceId={selectedLibrarySourceId}
            onSourceSelected={handleSourceSelected}
            onOpenZettel={handleSelectZettel}
            onLinkHighlight={handleLinkHighlight}
            refreshKey={refreshKey}
            resetKey={resetKey}
            onOpenCmd={handleOpenCmd}
            onHighlightEdited={handleHighlightEdited}
            onUnlinkFromNote={handleUnlinkHighlightFromNote}
            highlightUnlinkEvent={highlightUnlinkEvent}
            highlightLinkEvent={highlightLinkEvent}
            initialHighlightZettelMap={initialHighlightZettelMap}
            initialHighlightStatus={initialHighlightStatus}
            initialSyncing={initialSyncing}
            onQuickCreate={handleQuickCreateFromHighlight}
            focusedPanel={focusedPanel}
            onShowShortcutTip={showShortcutTip}
            onDetailChange={setLibraryDetailOpen}
            onHighlightFocusChange={setHighlightFocused}
            onHighlightEditingChange={setHighlightEditing}
            onHighlightCopied={handleHighlightCopied}
            readOnly={!isLicensed}
          />}
          <div className={`shortcut-hint-bar ${focusedPanel === 'library' ? 'visible' : ''}`}>
            {sidebarCollapsed ? (
              <span className="hint-group"><kbd>↵</kbd> Aufklappen</span>
            ) : libraryDetailOpen ? (
              highlightFocused ? (
                highlightEditing ? (
                  <>
                    <span className="hint-group"><kbd>⌘↵</kbd> Speichern</span>
                    <span className="hint-group"><kbd>Esc</kbd> Verwerfen</span>
                    <span className="hint-group"><kbd>⌘B</kbd> Fett</span>
                    <span className="hint-group"><kbd>⌘I</kbd> Kursiv</span>
                    <span className="hint-group"><kbd>⌘⇧H</kbd> Hervorheben</span>
                  </>
                ) : (
                  <>
                    <span className="hint-group"><kbd>↑↓</kbd> Navigieren</span>
                    <span className="hint-group"><kbd>↵</kbd> Bearbeiten</span>
                    <span className="hint-group"><kbd>→←</kbd> Auf/Zu</span>
                    <span className="hint-group"><kbd>L</kbd> Verlinken</span>
                    {highlightCopied ? (
                      <span className="hint-group hint-transient"><kbd>⌘V</kbd> Einfügen</span>
                    ) : (
                      <span className="hint-group"><kbd>⌘C</kbd> Kopieren</span>
                    )}
                  </>
                )
              ) : (
                <>
                  <span className="hint-group"><kbd>↑↓</kbd> Navigieren</span>
                  <span className="hint-group"><kbd>→←</kbd> Auf/Zu</span>
                  <span className="hint-group"><kbd>Esc</kbd> Zurück</span>
                </>
              )
            ) : (
              <>
                <span className="hint-group"><kbd>↑↓</kbd> Navigieren</span>
                <span className="hint-group"><kbd>↵</kbd> Öffnen</span>
              </>
            )}
          </div>
        </aside>

        {/* ===== MIDDLE: Structure ===== */}
        <section className={`panel panel-structure ${focusedPanel === 'tree' ? 'panel-focused' : ''}`} tabIndex={-1}
          onFocusCapture={() => { if (focusedPanel !== 'tree') setFocusedPanel('tree'); }}
          onMouseDown={(e) => {
            setFocusedPanel('tree');
            // Phase 4: First-use tips for tree interactions
            const target = e.target;
            if (target.closest('.tree-toggle')) {
              showShortcutTip('tree-chevron', <>Tipp: Drücke <kbd>→</kbd> und <kbd>←</kbd> zum Auf- und Zuklappen</>);
            } else if (target.closest('.tree-item') && !target.closest('.tree-toggle') && !target.closest('button')) {
              showShortcutTip('tree-open', <>Tipp: Drücke <kbd>↵</kbd> um den Zettel zu öffnen</>);
            }
          }}>
          <div className="structure-header">
            <div className="structure-nav">
              <div className="structure-toggle pill-nav">
                <button
                  className={`pill-nav-btn ${structureView === 'tree' ? 'active' : ''}`}
                  onClick={() => startTransition(() => setStructureView('tree'))}
                  title="Baumansicht"
                >
                  <IconZettelkasten />
                  <span className="pill-nav-label">Zettelkasten</span>
                </button>
                <button
                  className={`pill-nav-btn ${structureView === 'quotes' ? 'active' : ''}`}
                  onClick={() => startTransition(() => setStructureView('quotes'))}
                  title="Zitate"
                >
                  <IconQuote />
                  <span className="pill-nav-label">Zitate</span>
                </button>
                <button
                  className={`pill-nav-btn ${structureView === 'graph' ? 'active' : ''}`}
                  onClick={() => startTransition(() => setStructureView('graph'))}
                  title="Graph"
                >
                  <IconNetwork />
                  <span className="pill-nav-label">Graph</span>
                </button>
              </div>
              {structureView === 'tree' && (
                <button
                  className="icon-btn collapse-all-btn"
                  onClick={() => treeRef.current?.collapseAll()}
                  title="Alle zuklappen"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 14 10 14 10 20"/>
                    <polyline points="20 10 14 10 14 4"/>
                    <line x1="14" y1="10" x2="21" y2="3"/>
                    <line x1="3" y1="21" x2="10" y2="14"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {linkingMode && linkingHighlight && (
            <div className="tree-kb-move-hint">
              <span className="tree-kb-move-icon"><IconLink /></span>
              <span className="tree-kb-move-label">
                Highlight <strong>einfügen</strong>
              </span>
              <span className="tree-kb-move-keys">
                <kbd>↑↓</kbd> Position
                <kbd>→←</kbd> Öffnen/Schließen
                <kbd>↵</kbd> Einfügen
                <kbd>Esc</kbd> Abbrechen
              </span>
            </div>
          )}

          <div className="structure-view-container">
            <div className={`structure-view-layer ${structureView === 'tree' ? 'visible' : ''}`}>
              <ZettelTree
                ref={treeRef}
                allZettel={zettelList}
                selectedId={selectedZettel?.id}
                onSelect={handleTreeSelect}
                onAdd={linkingMode ? handleTreeAdd : null}
                mode={linkingMode}
                onCreateZettel={isLicensed ? handleTreeCreateZettel : null}
                onMove={handleMoveZettel}
                onHighlightDrop={handleHighlightDrop}
                canEdit={isLicensed}
              />
            </div>
            <div className={`structure-view-layer ${structureView === 'graph' ? 'visible' : ''}`}>
              <GraphView onSelectZettel={handleSelectZettel} visible={structureView === 'graph'} selectedId={selectedZettel?.id} graphLinkEvent={graphLinkEvent} />
            </div>
            <div className={`structure-view-layer ${structureView === 'quotes' ? 'visible' : ''}`}>
              <QuoteView quotes={quotes} onSelectZettel={(id, quoteText) => {
                handleSelectZettel(id).then(() => {
                  if (quoteText) {
                    setSelectedZettel(prev => prev ? { ...prev, _highlightQuote: quoteText } : prev);
                  }
                });
              }} visible={structureView === 'quotes'} selectedId={selectedZettel?.id} />
            </div>
          </div>
          {!linkingMode && (
            <div className={`shortcut-hint-bar ${focusedPanel === 'tree' ? 'visible' : ''}`}>
              {structureView === 'quotes' ? (
                <>
                  <span className="hint-group"><kbd>↑↓</kbd> Navigieren</span>
                  <span className="hint-group"><kbd>↵</kbd> Öffnen</span>
                  <span className="hint-group"><kbd>⌘2</kbd> Ansicht</span>
                </>
              ) : (
                <>
                  <span className="hint-group"><kbd>↑↓</kbd> Navigieren</span>
                  <span className="hint-group"><kbd>→←</kbd> Auf/Zu</span>
                  <span className="hint-group"><kbd>↵</kbd> Öffnen</span>
                  <span className="hint-group"><kbd>M</kbd> Verschieben</span>
                  <span className="hint-group"><kbd>⌘N</kbd> Neu</span>
                  <span className="hint-group"><kbd>⌘2</kbd> Ansicht</span>
                </>
              )}
            </div>
          )}
        </section>

        {/* ===== RIGHT: Content ===== */}
        <main className={`panel panel-content ${focusedPanel === 'editor' ? 'panel-focused' : ''}`} tabIndex={-1}
          onMouseDown={() => setFocusedPanel('editor')}
          onFocusCapture={() => { if (focusedPanel !== 'editor') setFocusedPanel('editor'); }}>
          <div className="content-header">
            {/* Zentrierter Titel — analog zu "Bibliothek" in der Sidebar */}
            <h1 className="content-title">Zettel</h1>
            <div className="nav-arrows">
              <button
                className="icon-btn nav-arrow-btn"
                onClick={handleGoBack}
                disabled={!canGoBack}
                title="Zurück"
                aria-label="Zurück navigieren"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button
                className="icon-btn nav-arrow-btn"
                onClick={handleGoForward}
                disabled={!canGoForward}
                title="Vorwärts"
                aria-label="Vorwärts navigieren"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
            <div className="content-actions">
              <button
                className="icon-btn cheat-sheet-btn"
                onClick={() => setCheatSheetOpen(prev => !prev)}
                title="Tastaturkürzel (⌘/)"
                aria-label="Tastaturkürzel anzeigen"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"/>
                  <path d="M6 9h.01M10 9h.01M14 9h.01M18 9h.01M6 13h.01M10 13h.01M14 13h4M6 17h12"/>
                </svg>
              </button>
              <button className="icon-btn" title="Einstellungen" aria-label="Einstellungen"
                onClick={() => setSettingsOpen(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </button>
              <button className="icon-btn" title="Theme wechseln" aria-label="Theme wechseln"
                onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}>
                {theme === 'dark' ? <IconSun /> : <IconMoon />}
              </button>
            </div>
          </div>

          {/* Linking Banner entfernt — Info ist im Tree-Hint-Bar sichtbar */}

          {/* Breadcrumb */}
          {selectedZettel && (
            <div className="zettel-breadcrumb">
              {breadcrumbPath.map((id, i) => (
                <React.Fragment key={id}>
                  {i > 0 && <span className="breadcrumb-sep">›</span>}
                  <button className={`breadcrumb-item ${i === breadcrumbPath.length - 1 ? 'active' : ''}`}
                    onClick={() => handleSelectZettel(id)}>{id}</button>
                </React.Fragment>
              ))}
              <div style={{ flex: 1 }} />
              {isLicensed && (
                <button className="breadcrumb-delete-btn" onClick={handleDeleteZettel} title="Zettel löschen">
                  <IconTrash />
                </button>
              )}
            </div>
          )}

          <ZettelEditor
            key={selectedZettel?.id || '__empty'}
            zettel={selectedZettel}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSaved={handleEditorSaved}
            onNavigate={handleSelectZettel}
            allZettel={zettelList}
            onOpenReadwise={handleOpenReadwise}
            linkingHighlight={linkingMode === 'newBranch' ? linkingHighlight : null}
            onLinkToExisting={noopCallback}
            flushSaveRef={flushSaveRef}
            highlightPatch={highlightPatch}
            onHighlightDrop={handleHighlightDrop}
            getHighlightData={getHighlightData}
            newNoteMode={newNoteMode}
            onNewNoteSaved={handleNewNoteSaved}
            onHighlightUnlinked={handleHighlightUnlinked}
            onHighlightLinked={handleHighlightLinked}
            editorFocusKey={editorFocusKey}
            onFocusPanel={(panel) => { setFocusedPanel(panel); if (panel === 'tree') { requestAnimationFrame(() => document.querySelector('.zettel-tree')?.focus()); } }}
            readOnly={!isLicensed}
          />
          <div className={`shortcut-hint-bar ${focusedPanel === 'editor' ? 'visible' : ''}`}>
            {selectedZettel ? (
              <>
                <span className="hint-group"><kbd>⌘⇧Z</kbd> Zitat</span>
                <span className="hint-group"><kbd>⌘V</kbd> Einfügen</span>
                <span className="hint-group"><kbd>⌘2</kbd> Baum</span>
                <span className="hint-group"><kbd>⌘1</kbd> Bibliothek</span>
                <span className="hint-group"><kbd>⌘K</kbd> Suche</span>
              </>
            ) : (
              <>
                <span className="hint-group"><kbd>⌘1</kbd> Bibliothek</span>
                <span className="hint-group"><kbd>⌘2</kbd> Zettelkasten</span>
                <span className="hint-group"><kbd>⌘N</kbd> Neuer Zettel</span>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Sidebar expand button removed — collapsed strip is inside the sidebar now */}

      {/* Lösch-Bestätigung bei Kaskade */}
      {deleteConfirm && (
        <div className="delete-confirm-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="delete-confirm-header">
              <span className="delete-confirm-title">In den Papierkorb verschieben?</span>
            </div>
            <div className="delete-confirm-body">
              <div className="delete-confirm-main">
                <span className="delete-confirm-id">{deleteConfirm.zettel.id}</span>
                <span>{deleteConfirm.zettel.title || '(Ohne Titel)'}</span>
              </div>
              <div className="delete-confirm-list">
                {deleteConfirm.descendants.map(z => (
                  <div key={z.id} className="delete-confirm-item">
                    <span className="delete-confirm-id">{z.id}</span>
                    <span>{z.title || '(Ohne Titel)'}</span>
                  </div>
                ))}
              </div>
              {deleteConfirm.referencedBy.length > 0 && (
                <p className="delete-confirm-warning">
                  Wird von {deleteConfirm.referencedBy.length} {deleteConfirm.referencedBy.length === 1 ? 'Notiz' : 'Notizen'} verlinkt. Die Verweise bleiben bestehen.
                </p>
              )}
            </div>
            <div className="delete-confirm-actions">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Abbrechen</button>
              <button className="btn-danger" onClick={() => {
                executeDelete(deleteConfirm.zettel, deleteConfirm.descendants);
                setDeleteConfirm(null);
              }}>
                {deleteConfirm.descendants.length + 1} {deleteConfirm.descendants.length === 0 ? 'Notiz' : 'Notizen'} in den Papierkorb
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cheat Sheet */}
      {cheatSheetOpen && (
        <div className="cheat-sheet-overlay" onClick={() => setCheatSheetOpen(false)}>
          <div className="cheat-sheet" onClick={e => e.stopPropagation()}>
            <div className="cheat-sheet-header">
              <span className="cheat-sheet-title">Tastaturkürzel</span>
              <button className="cheat-sheet-close" onClick={() => setCheatSheetOpen(false)}>✕</button>
            </div>
            <div className="cheat-sheet-body">
              <div className="cheat-sheet-section">
                <div className="cheat-sheet-section-title">PANELS</div>
                <div className="cheat-sheet-row"><kbd>⌘ 1</kbd><span>Bibliothek</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ 2</kbd><span>Zettelbaum</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ 3</kbd><span>Editor</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ K</kbd><span>Suche &amp; Aktionen</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ F</kbd><span>Suchfeld fokussieren</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ ,</kbd><span>Einstellungen</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ N</kbd><span>Neuer Zettel</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ /</kbd><span>Diese Übersicht</span></div>
                <div className="cheat-sheet-row"><kbd>Esc</kbd><span>Zurück / Schließen</span></div>
              </div>
              <div className="cheat-sheet-section">
                <div className="cheat-sheet-section-title">IM ZETTELBAUM</div>
                <div className="cheat-sheet-row"><kbd>↑</kbd><kbd>↓</kbd><span>Navigieren</span></div>
                <div className="cheat-sheet-row"><kbd>→</kbd><span>Aufklappen</span></div>
                <div className="cheat-sheet-row"><kbd>←</kbd><span>Einklappen / zum Elternteil</span></div>
                <div className="cheat-sheet-row"><kbd>↵</kbd><span>Zettel öffnen</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ ⇧ M</kbd><span>Verschieben / Einfügen</span></div>
              </div>
              <div className="cheat-sheet-section">
                <div className="cheat-sheet-section-title">NEUER ZETTEL (⌘ N)</div>
                <div className="cheat-sheet-row"><kbd>↑</kbd><kbd>↓</kbd><span>Position wählen</span></div>
                <div className="cheat-sheet-row"><kbd>→</kbd><kbd>←</kbd><span>Öffnen / Schließen</span></div>
                <div className="cheat-sheet-row"><kbd>↵</kbd><span>Erstellen</span></div>
                <div className="cheat-sheet-row"><kbd>Esc</kbd><span>Abbrechen</span></div>
              </div>
              <div className="cheat-sheet-section">
                <div className="cheat-sheet-section-title">BEIM VERSCHIEBEN (⌘ ⇧ M)</div>
                <div className="cheat-sheet-row"><kbd>↑</kbd><kbd>↓</kbd><span>Zielposition wählen</span></div>
                <div className="cheat-sheet-row"><kbd>→</kbd><span>Als Kind einbetten</span></div>
                <div className="cheat-sheet-row"><kbd>←</kbd><span>Einbetten aufheben</span></div>
                <div className="cheat-sheet-row"><kbd>↵</kbd><span>Bestätigen</span></div>
                <div className="cheat-sheet-row"><kbd>Esc</kbd><span>Abbrechen</span></div>
              </div>
              <div className="cheat-sheet-section">
                <div className="cheat-sheet-section-title">IN DER BIBLIOTHEK</div>
                <div className="cheat-sheet-row"><kbd>↑</kbd><kbd>↓</kbd><span>Quelle / Highlight wählen</span></div>
                <div className="cheat-sheet-row"><kbd>↵</kbd><span>Öffnen / Auf-/Einklappen</span></div>
                <div className="cheat-sheet-row"><kbd>E</kbd><span>Highlight bearbeiten</span></div>
                <div className="cheat-sheet-row"><kbd>L</kbd><span>Mit Zettel verknüpfen</span></div>
                <div className="cheat-sheet-row"><kbd>⌘ ⇧ M</kbd><span>Highlight in Zettel einfügen</span></div>
                <div className="cheat-sheet-row"><kbd>Esc</kbd><span>Zurück zur Quellenliste</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Command Palette */}
      <div className={`cmd-overlay ${cmdOpen ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setCmdOpen(false); }}>
        <div className="cmd-palette" onKeyDown={(e) => {
          if (e.key === 'Tab') {
            const focusable = e.currentTarget.querySelectorAll('input, button, [tabindex]:not([tabindex="-1"])');
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
              if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
              }
            } else {
              if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
              }
            }
          }
        }}>
          <div className="cmd-search-wrapper">
            <IconSearch />
            <input
              ref={cmdInputRef}
              className="cmd-search-input"
              type="text"
              placeholder="Suche nach Notizen… oder &gt; für Aktionen"
              value={cmdQuery}
              onChange={(e) => setCmdQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setCmdActiveIndex(i => Math.min(i + 1, cmdResults.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setCmdActiveIndex(i => Math.max(i - 1, 0));
                } else if (e.key === 'Enter' && cmdResults.length > 0) {
                  handleCmdSelect(cmdResults[cmdActiveIndex]);
                }
              }}
            />
          </div>
          <div className="cmd-results">
            <div className="cmd-group-label">
              {cmdQuery.startsWith('>') ? 'Aktionen' : cmdQuery.trim() ? 'Ergebnisse' : 'Zuletzt bearbeitet'}
            </div>
            {cmdResults.map((item, index) => (
              <div key={item._uid}
                className={`cmd-result-item ${index === cmdActiveIndex ? 'active' : ''}`}
                onClick={() => handleCmdSelect(item)}
                onMouseEnter={() => setCmdActiveIndex(index)}
              >
                <span className="cmd-result-icon">
                  {item.kind === 'action' ? item.icon : <IconFileText />}
                </span>
                <span className="cmd-result-title">
                  {item.kind === 'action' ? item.label : (item.title || '(Ohne Titel)')}
                  {item.kind === 'literatur' && item.author && (
                    <span className="cmd-result-author"> · {item.author}</span>
                  )}
                </span>
                {item.kind === 'permanent' && <span className="cmd-result-id">{item.id}</span>}
                {item.kind === 'literatur' && <span className="cmd-result-kind cmd-result-kind--literatur">Literatur</span>}
                {item.kind === 'action' && <span className="cmd-result-kind">Aktion</span>}
              </div>
            ))}
            {cmdResults.length === 0 && cmdQuery && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
                Keine Ergebnisse
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Graph Overlay */}
      {showGraph && (
        <div className="graph-overlay">
          <button className="graph-overlay-close" onClick={() => setShowGraph(false)} title="Graph schließen">✕</button>
          <GraphView onSelectZettel={(id) => { handleSelectZettel(id); setShowGraph(false); }} />
        </div>
      )}

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        activeNote={selectedZettel}
        onReset={() => {
          loadZettel();
          refreshLibrary();
          setSelectedZettel(null);
          setResetKey(k => k + 1);
        }}
        onLicenseChange={handleLicenseChange}
      />

      {/* Drop-Toast Notification */}
      {dropToast && (
        <div className={`drop-toast drop-toast--${dropToast.type}`} role="status" aria-live="polite">
          <span className="drop-toast-icon">{dropToast.type === 'error' ? '✗' : '✓'}</span>
          <span>{dropToast.msg}</span>
        </div>
      )}

      {/* Undo-Toast für Zettel-Löschen (#36) */}
      {undoAction && (
        <div className="undo-toast" role="status" aria-live="polite">
          <span>{undoAction.message}</span>
          <button onClick={undoAction.undo} className="undo-toast-btn">Rückgängig</button>
        </div>
      )}

      {/* Update-Banner (#87) */}
      {updateInfo && (
        <div className="update-banner" role="status" aria-live="polite">
          <span className="update-banner-text">
            Luhm <strong>v{updateInfo.latest}</strong> ist verfügbar{updateInfo.notes ? ` — ${updateInfo.notes}` : ''}
          </span>
          <a
            href={updateInfo.url || 'https://luhm.app/download'}
            target="_blank"
            rel="noopener noreferrer"
            className="update-banner-btn"
          >
            Herunterladen
          </a>
          <button
            className="update-banner-close"
            aria-label="Später erinnern"
            onClick={() => {
              setUpdateInfo(null);
              localStorage.setItem('updateDismissed', String(Date.now()));
            }}
          >✕</button>
        </div>
      )}

      {/* Phase 4: First-Use Shortcut Tips */}
      {shortcutTip && (
        <div className="shortcut-tip" role="status" aria-live="polite">
          {shortcutTip.message}
        </div>
      )}
    </>
  );
}

export default App;
